import { useState, useEffect } from 'react';
import { LOADER_TEMPO, REDUCED_MOTION_SLOWDOWN, type BeatPattern } from '../../../src/constants/danceLoaders';
import { usePrefersReducedMotion } from '../../../src/hooks/usePrefersReducedMotion';
import './DanceLoaders.css';

interface BeatLoaderProps {
  pattern: BeatPattern;
}

// Lights one beat of a dance count at a time, holding each for its step duration.
// With reduced motion nothing changes size: the accent beat stays printed big and
// the light walks the count slower, by colour only (#1940).
export function BeatLoader({ pattern }: BeatLoaderProps) {
  const [step, setStep] = useState(0);
  const reduced = usePrefersReducedMotion();
  const { beats, steps, accent } = pattern;

  useEffect(() => {
    const ms = (steps[step][1] / LOADER_TEMPO) * (reduced ? REDUCED_MOTION_SLOWDOWN : 1);
    const timer = setTimeout(() => setStep((s) => (s + 1) % steps.length), ms);
    return () => clearTimeout(timer);
  }, [step, steps, reduced]);

  const active = steps[step][0];

  return (
    <div className="dance-text-loader" role="status" aria-label="Loading…">
      {beats.map((beat, i) => {
        const isActive = active === i;
        const big = reduced ? i === accent : isActive;
        const [fontSize, fontWeight] = big ? beat.on : beat.off;
        return (
          <span
            key={i}
            aria-hidden="true"
            className={`dance-beat ${isActive ? 'dance-beat--active' : 'dance-beat--rest'}`}
            style={{
              fontSize: `${fontSize}rem`,
              fontWeight,
              fontStyle: beat.italic ? 'italic' : 'normal',
              letterSpacing: big && beat.track ? '0.05em' : undefined,
            }}
          >
            {beat.label}
          </span>
        );
      })}
    </div>
  );
}
